import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Listbox, Transition } from "@headlessui/react";
import { ReactComponent as Chevron } from "../assets/icons/Chevron.svg";
import { ReactComponent as BackArrow } from "../assets/icons/BackArrow.svg";
import TextInput from "../components/TextInput";
import GenderSelect from "../components/GenderSelect";
import DOBSelect from "../components/DOBSelect";
import { DEFAULT_SCAN_DURATION, SCAN_DURATION_OPTIONS } from "../utils/scanDuration";

const BasicDetails = () => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();

  const phoneRef = useRef(null);
  const nameRef = useRef(null);
  const otherRelationRef = useRef(null);
  const heightRef = useRef(null);
  const weightRef = useRef(null);

  const [phoneNumber, setPhoneNumber] = useState(locationState?.phoneNumber || "");
  const [phoneError, setPhoneError] = useState("");
  const [personName, setPersonName] = useState("");
  const [nameError, setNameError] = useState("");
  const [relation, setRelation] = useState("Self");
  const [otherRelation, setOtherRelation] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDOB] = useState("");
  const [isHeightInCm, setHeightInCm] = useState(false);
  const [heightCm, setHeightCm] = useState("");
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [isWeightInKg, setWeightInKg] = useState(true);
  const [weightKg, setWeightKg] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [scanDuration, setScanDuration] = useState(
    locationState?.scanDuration || DEFAULT_SCAN_DURATION
  );
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [weightLbs, setWeightLbs] = useState("");

  const [relationError, setRelationError] = useState("");
  const [genderError, setGenderError] = useState("");
  const [dobError, setDOBError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [weightError, setWeightError] = useState("");

  const [usedRelations, setUsedRelations] = useState([]);
  const [scanLimitData, setScanLimitData] = useState(null);

  useEffect(() => {
    setRelationError("");
    if (relation !== "Other") setOtherRelation("");
  }, [relation]);

  useEffect(() => {
    const trimPhone = phoneNumber.trim().replace(/[^0-9]/g, "");
    if (trimPhone.length === 10) {
      fetch("/api/check-scan-limit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone_number: trimPhone }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.success) {
            setScanLimitData(data.data);
            setUsedRelations(data.data.used_relations || []);
            if (["Self", "Father", "Mother"].includes(relation) && data.data.used_relations?.includes(relation)) {
              const available = data.data.available_relations || [];
              if (available.length > 0) setRelation(available[0]);
              else if (data.data.remaining > 0) setRelation("Other");
            }
          }
        })
        .catch(() => {});
    } else {
      setUsedRelations([]);
      setScanLimitData(null);
    }
  }, [phoneNumber]);

  const proceed = (e) => {
    e.preventDefault();
    setPhoneError("");
    setNameError("");
    setRelationError("");
    setGenderError("");
    setDOBError("");
    setHeightError("");
    setWeightError("");
    let error = "";
    const trimPhone = phoneNumber.trim().replace(/[^0-9]/g, "");
    if (trimPhone.length !== 10) {
      setPhoneError("Please enter a valid 10-digit phone number.");
      error = "phone";
    }
    setPhoneNumber(trimPhone);
    const trimName = personName.trim();
    if (trimName.length === 0) {
      setNameError("Please enter the person's name.");
      if (!error) error = "name";
    }
    setPersonName(trimName);
    let trim_other_relation = otherRelation.trim().replace(/[^a-zA-Z ]/g, "");
    trim_other_relation = trim_other_relation.charAt(0).toUpperCase() + trim_other_relation.slice(1);
    if (relation.length === 0) {
      setRelationError("Please select your Relation.");
      error = "relation";
    } else if (relation === "Other" && trim_other_relation.length === 0) {
      setRelationError("Please enter your Relation.");
      error = "other-relation";
    } else if (["Self", "Father", "Mother"].includes(relation) && usedRelations.includes(relation)) {
      setRelationError(`A scan for "${relation}" already exists for this phone number.`);
      if (!error) error = "relation";
    }
    setOtherRelation(trim_other_relation);
    const trim_weight_kg = weightKg.trim();
    const trim_weight_lbs = weightLbs.trim();
    if (/^\d{1,3}(\.\d{1,2})?$/.test(isWeightInKg ? trim_weight_kg : trim_weight_lbs)) {
      if (trim_weight_kg < 20 || trim_weight_kg > 220) {
        if (isWeightInKg) setWeightError("Weight must be between 20 and 220 kgs.");
        else setWeightError("Weight must be between 44.09 and 485.02 lbs.");
        error = "weight";
      }
    } else {
      setWeightError("Please enter valid Weight.");
      error = "weight";
    }
    setWeightKg(trim_weight_kg);
    setWeightLbs(trim_weight_lbs);
    const trim_height_cms = heightCm.trim();
    const trim_height_ft = heightFt.trim();
    const trim_height_in = heightIn.trim();
    if (
      isHeightInCm
        ? /^\d{1,3}(\.\d{1,2})?$/.test(trim_height_cms)
        : /^\d$/.test(trim_height_ft) && /^(?:\d|1[01])$/.test(trim_height_in)
    ) {
      if (trim_height_cms < 30 || trim_height_cms > 250) {
        if (isHeightInCm) setHeightError("Height must be between 30 and 250 cms.");
        else setHeightError("Height must be between 1ft and 8ft 2inches.");
        error = "height";
      }
    } else {
      setHeightError("Please enter valid Height.");
      error = "height";
    }
    setHeightCm(trim_height_cms);
    setHeightFt(trim_height_ft);
    setHeightIn(trim_height_in);
    if (error !== "height" && error !== "weight") {
      const bmi = parseFloat(trim_weight_kg) / (parseFloat(trim_height_cms) / 100) ** 2;
      if (bmi < 10 || bmi > 100) {
        setHeightError("Please enter valid Height.");
        setWeightError("Please enter valid Weight.");
        error = "bmi";
      }
    }
    if (dob.length === 0) {
      setDOBError("Please select your Date of Birth.");
      error = "dob";
    }
    if (!/^(male|female|others)$/.test(gender)) {
      setGenderError("Please select your Gender.");
      error = "gender";
    }
    if (error.length > 0)
      switch (error) {
        case "phone":
          phoneRef.current?.focus?.();
          break;
        case "name":
          nameRef.current?.focus?.();
          break;
        case "other-relation":
          otherRelationRef.current?.focus?.();
          break;
        case "height":
          heightRef.current?.focus?.();
          break;
        case "weight":
          weightRef.current?.focus?.();
          break;
        default:
          break;
      }
    else
      navigate("/select-option", {
        state: {
          scanDuration,
          userData: {
            phone_number: trimPhone,
            person_name: trimName,
            relation: relation === "Other" ? trim_other_relation : relation,
            gender: gender.toLowerCase(),
            dob: dob,
            height: parseFloat(trim_height_cms),
            weight: parseFloat(trim_weight_kg),
          },
        },
      });
  };

  return (
    <section className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-[#1845a3] to-[#ED1C24] px-5 pt-5 pb-10">
        <div className="flex items-center">
          <Link className="flex-shrink-0 p-1 pr-3" to={-1}>
            <BackArrow className="h-2.5 w-2.5 text-white" />
          </Link>
          <h3 className="text-sm font-semibold text-white tracking-wide">Basic Details</h3>
        </div>
        <p className="mt-2 ml-6 text-white/60 text-xxs">Please fill in your information to proceed</p>
      </div>

      <div className="px-5 -mt-6 pb-6">
        <div className="rounded-2xl bg-white shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-5 space-y-4">
            <div>
              <label htmlFor="phone" className="text-xs font-semibold text-gray-700">Mobile Number</label>
              <div className="relative mt-1.5">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <input
                  ref={phoneRef}
                  id="phone"
                  name="phone"
                  className={`block w-full pl-9 pr-4 py-2.5 rounded-xl outline-none border ${
                    phoneError.length > 0 ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
                  } focus:border-[#1845a3] focus:bg-white text-gray-800 text-xs transition-colors`}
                  placeholder="Enter 10-digit mobile number"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneError("");
                    setPhoneNumber(e.target.value.replace(/[^0-9]/g, ""));
                  }}
                />
              </div>
              {phoneError.length > 0 && <p className="mt-1 text-xxs text-red-500">{phoneError}</p>}
            </div>

            <div>
              <label htmlFor="person-name" className="text-xs font-semibold text-gray-700">Full Name</label>
              <div className="relative mt-1.5">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  ref={nameRef}
                  id="person-name"
                  name="person-name"
                  className={`block w-full pl-9 pr-4 py-2.5 rounded-xl outline-none border ${
                    nameError.length > 0 ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
                  } focus:border-[#1845a3] focus:bg-white text-gray-800 text-xs transition-colors`}
                  placeholder="Enter full name"
                  type="text"
                  value={personName}
                  onChange={(e) => {
                    setNameError("");
                    setPersonName(e.target.value);
                  }}
                />
              </div>
              {nameError.length > 0 && <p className="mt-1 text-xxs text-red-500">{nameError}</p>}
            </div>

            <Listbox as="div" className="relative" value={relation} onChange={setRelation}>
              <Listbox.Label className="text-xs font-semibold text-gray-700">Relation</Listbox.Label>
              <Listbox.Button className="w-full flex items-center justify-between space-x-4 mt-1.5 pl-3 pr-3 py-2.5 rounded-xl outline-none border border-gray-200 focus:border-[#1845a3] bg-gray-50 focus:bg-white text-gray-800 transition-colors">
                {({ open }) => (
                  <>
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-xs text-left">{relation}</span>
                    </div>
                    <Chevron
                      className={`shrink-0 h-3.5 w-3.5 text-gray-400 transition-transform duration-300 ${open ? "-rotate-180" : "rotate-0"}`}
                    />
                  </>
                )}
              </Listbox.Button>
              <Transition
                as={Fragment}
                enter="transition duration-300 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-200 delay-100 ease-in"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <Listbox.Options className="absolute z-50 w-full mt-1 py-1 shadow-lg rounded-xl border border-gray-200 bg-white text-gray-800 text-xs max-h-60 overflow-y-auto">
                  {["Self", "Father", "Mother", "Brother", "Sister", "Spouse", "Uncle", "Aunty", "Other"].map((rel, idx) => {
                    const isReserved = ["Self", "Father", "Mother"].includes(rel);
                    const isUsed = isReserved && usedRelations.includes(rel);
                    return (
                      <Listbox.Option
                        key={`relation-${idx}`}
                        value={rel}
                        disabled={isUsed}
                        className={`px-4 py-2.5 ${isUsed ? "opacity-40 cursor-not-allowed bg-gray-50" : "active:bg-gradient-to-r active:from-[#1845a3] active:to-[#ED1C24] active:text-white hover:bg-gray-50 cursor-pointer"}`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{rel}</span>
                          {isUsed && <span className="text-xxs text-red-400 font-medium">Already scanned</span>}
                        </div>
                      </Listbox.Option>
                    );
                  })}
                </Listbox.Options>
              </Transition>
              {relation === "Other" && (
                <TextInput
                  inputRef={otherRelationRef}
                  id="other-relation"
                  error={relationError}
                  placeholder="Enter your Relation"
                  value={otherRelation}
                  onChangeText={(rel) => {
                    setRelationError("");
                    setOtherRelation(rel);
                  }}
                />
              )}
            </Listbox>
            {relationError.length > 0 && relation !== "Other" && <p className="-mt-2 text-xxs text-red-500">{relationError}</p>}

            <GenderSelect
              label="Gender"
              onSelect={(selectedGender) => {
                setGenderError("");
                setGender(selectedGender);
              }}
              value={gender}
              error={genderError}
            />
            <DOBSelect
              id="dob"
              label="Date of Birth"
              placeholder="Select your Date of Birth"
              onSelect={(date) => {
                setDOBError("");
                setDOB(date);
              }}
              value={dob}
              error={dobError}
            />

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="height" className="text-xs font-semibold text-gray-700">
                  Height ({isHeightInCm ? "cms" : "ft/inches"})
                </label>
                <div className="flex items-stretch overflow-hidden rounded-lg border border-gray-200">
                  <button
                    type="button"
                    className={`px-3 py-1 transition-all duration-300 ${
                      isHeightInCm ? "bg-transparent text-gray-500" : "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white"
                    } text-xxs font-medium`}
                    onClick={() => {
                      setHeightInCm(false);
                      setHeightError("");
                    }}
                  >
                    ft/in
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 transition-all duration-300 ${
                      isHeightInCm ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white" : "bg-transparent text-gray-500"
                    } text-xxs font-medium`}
                    onClick={() => {
                      setHeightInCm(true);
                      setHeightError("");
                    }}
                  >
                    cms
                  </button>
                </div>
              </div>
              {isHeightInCm ? (
                <input
                  ref={heightRef}
                  id="height"
                  name="height"
                  className={`block w-full mt-1.5 px-4 py-2.5 rounded-xl outline-none border ${
                    heightError?.length > 0 ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
                  } focus:border-[#1845a3] focus:bg-white text-gray-800 text-xs transition-colors`}
                  placeholder="Enter your Height in cms"
                  type="text"
                  inputMode="decimal"
                  value={heightCm}
                  onChange={(e) => {
                    const h = e.target.value;
                    setHeightCm(h);
                    const inchesTotal = parseFloat(h) / 2.54;
                    if (isNaN(inchesTotal)) {
                      setHeightFt("");
                      setHeightIn("");
                    } else {
                      const feet = Math.floor(inchesTotal / 12);
                      const inches = Math.round(inchesTotal % 12);
                      const isIn12 = inches === 12;
                      setHeightFt((feet + isIn12).toFixed(0));
                      setHeightIn(isIn12 ? "0" : inches.toFixed(0));
                    }
                  }}
                />
              ) : (
                <div className="mt-1.5 flex items-stretch justify-between space-x-3">
                  <input
                    ref={heightRef}
                    id="height"
                    name="height"
                    className={`w-[46%] px-4 py-2.5 rounded-xl outline-none border ${
                      heightError?.length > 0 ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
                    } focus:border-[#1845a3] focus:bg-white text-gray-800 text-xs transition-colors`}
                    placeholder="Feet"
                    type="text"
                    inputMode="decimal"
                    value={heightFt}
                    onChange={(e) => {
                      const hft = e.target.value;
                      setHeightFt(hft);
                      const cms = (parseInt(hft) * 12 + (parseInt(heightIn) || 0)) * 2.54;
                      setHeightCm(isNaN(cms) ? "" : cms.toFixed(2));
                    }}
                  />
                  <input
                    id="height2"
                    name="height2"
                    className={`w-[54%] px-4 py-2.5 rounded-xl outline-none border ${
                      heightError?.length > 0 ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
                    } focus:border-[#1845a3] focus:bg-white text-gray-800 text-xs transition-colors`}
                    placeholder="Inches"
                    type="text"
                    inputMode="decimal"
                    value={heightIn}
                    onChange={(e) => {
                      const hin = e.target.value;
                      setHeightIn(hin);
                      const cms = ((parseInt(heightFt) || 0) * 12 + parseInt(hin)) * 2.54;
                      setHeightCm(isNaN(cms) ? "" : cms.toFixed(2));
                    }}
                  />
                </div>
              )}
              {heightError?.length > 0 && <p className="mt-1 text-xxs text-red-500">{heightError}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="weight" className="text-xs font-semibold text-gray-700">
                  Weight ({isWeightInKg ? "kgs" : "lbs"})
                </label>
                <div className="flex items-stretch overflow-hidden rounded-lg border border-gray-200">
                  <button
                    type="button"
                    className={`px-3 py-1 transition-all duration-300 ${
                      isWeightInKg ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white" : "bg-transparent text-gray-500"
                    } text-xxs font-medium`}
                    onClick={() => {
                      setWeightInKg(true);
                      setWeightError("");
                    }}
                  >
                    kgs
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 transition-all duration-300 ${
                      isWeightInKg ? "bg-transparent text-gray-500" : "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white"
                    } text-xxs font-medium`}
                    onClick={() => {
                      setWeightInKg(false);
                      setWeightError("");
                    }}
                  >
                    lbs
                  </button>
                </div>
              </div>
              <input
                ref={weightRef}
                id="weight"
                name="weight"
                className={`block w-full mt-1.5 px-4 py-2.5 rounded-xl outline-none border ${
                  weightError.length > 0 ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
                } focus:border-[#1845a3] focus:bg-white text-gray-800 text-xs transition-colors`}
                placeholder={`Enter your Weight in ${isWeightInKg ? "kgs" : "lbs"}`}
                type="text"
                inputMode="decimal"
                value={isWeightInKg ? weightKg : weightLbs}
                onChange={(e) => {
                  const w = e.target.value;
                  if (isWeightInKg) {
                    setWeightKg(w);
                    const lbs = parseFloat(w) * 2.20462;
                    setWeightLbs(isNaN(lbs) ? "" : lbs.toFixed(2));
                  } else {
                    setWeightLbs(w);
                    const kgs = parseFloat(w) / 2.20462;
                    setWeightKg(isNaN(kgs) ? "" : kgs.toFixed(2));
                  }
                }}
              />
              {weightError.length > 0 && <p className="mt-1 text-xxs text-red-500">{weightError}</p>}
            </div>
          </div>

          <div className="px-5 pb-2">
            <fieldset className="mb-4">
              <legend className="text-xs font-semibold text-gray-700">Scan duration</legend>
              <p className="mt-1 text-xxs text-gray-400">
                Choose 60 seconds for the complete report, including HRV, ANS and stress-related insights.
              </p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                {SCAN_DURATION_OPTIONS.map((duration) => (
                  <label
                    key={duration}
                    className={`cursor-pointer rounded-xl border p-3 text-center transition-all ${
                      scanDuration === duration
                        ? "border-[#1845a3] bg-[#1845a3]/5 shadow-sm"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="scan-duration"
                      value={duration}
                      checked={scanDuration === duration}
                      onChange={() => setScanDuration(duration)}
                      className="sr-only"
                    />
                    <span className="block text-sm font-bold text-gray-800">{duration} seconds</span>
                    <span className="mt-0.5 block text-xxs text-gray-400">
                      {duration === 60 ? "Complete insights" : "Basic vitals"}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="flex items-start space-x-2.5 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#1845a3] cursor-pointer"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <span className="text-xxs text-gray-500 leading-relaxed">
                I give my{" "}
                <button
                  type="button"
                  className="text-[#1845a3] underline font-medium focus:outline-none"
                  onClick={() => setShowConsentModal(true)}
                >
                  consent
                </button>
                {" "}to the collection and processing of my personal data
              </span>
            </label>
          </div>

          <div className="px-5 pb-5">
            {scanLimitData?.limit_reached && phoneNumber.trim().replace(/[^0-9]/g, "").length === 10 ? (
              <div className="text-center">
                <div className="py-3 px-4 rounded-xl bg-red-50 border border-red-200 mb-3">
                  <p className="text-xs text-red-600 font-medium">Maximum scan limit (10) reached for this phone number.</p>
                </div>
                <button
                  className="w-full py-3 rounded-xl bg-gray-300 text-white text-xs font-semibold cursor-not-allowed"
                  type="button"
                  disabled
                >
                  Scan Limit Reached
                </button>
              </div>
            ) : (
              <>
                {scanLimitData && phoneNumber.trim().replace(/[^0-9]/g, "").length === 10 && (
                  <p className="text-xxs text-gray-400 text-center mb-2">{scanLimitData.remaining} of 10 scans remaining for this phone number</p>
                )}
                <button
                  className={`w-full py-3 rounded-xl text-white text-xs font-semibold shadow-lg transition-opacity ${termsAccepted ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24]" : "bg-gray-300 cursor-not-allowed"}`}
                  type="button"
                  onClick={proceed}
                  disabled={!termsAccepted}
                >
                  Proceed
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showConsentModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-sm font-bold text-[#1845a3]">Customer Consent</h2>
              <button type="button" onClick={() => setShowConsentModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-4 space-y-3 text-xxs text-gray-600 leading-relaxed">
              <p className="font-semibold text-xs text-gray-800">Customer Consent for Collection and Processing of Personal Data</p>
              <p>By proceeding, I hereby provide my free, specific, informed, and unambiguous consent to Indusind General Insurance ("Company") for the collection, processing, storage, and use of my personal data, including health-related information, as per applicable laws.</p>
              <p className="text-gray-400 italic">I understand and acknowledge that:</p>

              <div>
                <p className="font-semibold text-gray-800 mb-1">Data Collection</p>
                <p>The Company may collect personal information including my contact details, demographic details, and health-related information through its authorized platform and partners.</p>
              </div>

              <div>
                <p className="font-semibold text-gray-800 mb-1">Purpose of Processing</p>
                <p className="mb-1">My personal data may be used for the following purposes:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Lead generation and product outreach</li>
                  <li>Risk assessment and underwriting of insurance proposals</li>
                  <li>Policy issuance, servicing, and claims processing</li>
                  <li>Compliance with legal and regulatory requirements</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-gray-800 mb-1">Data Sharing</p>
                <p className="mb-1">My information may be shared with:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Authorized agents/intermediaries of the Company</li>
                  <li>Technology service providers / vendors engaged by the Company</li>
                  <li>Regulatory authorities, as required</li>
                </ul>
                <p className="mt-1">Such parties shall process my data strictly on behalf of the Company and under appropriate confidentiality and security obligations.</p>
              </div>

              <div>
                <p className="font-semibold text-gray-800 mb-1">Data Storage &amp; Security</p>
                <p>My data will be securely stored by the Company and/or its authorized service providers with appropriate safeguards.</p>
              </div>

              <div>
                <p className="font-semibold text-gray-800 mb-1">Voluntary Provision of Data</p>
                <p>I understand that providing my personal data is voluntary; however, refusal may impact the Company's ability to offer insurance products/services.</p>
              </div>

              <div>
                <p className="font-semibold text-gray-800 mb-1">My Rights</p>
                <p className="mb-1">I have the right to:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Access and correct my personal data</li>
                  <li>Withdraw my consent at any time</li>
                  <li>Request erasure of my data, subject to legal and regulatory requirements</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-gray-800 mb-1">Consent Withdrawal</p>
                <p>I understand that I may withdraw my consent by contacting the Company through the prescribed channels, after which the Company shall cease processing my data, except where required by law.</p>
              </div>
            </div>
            <div className="flex-shrink-0 px-5 py-4 border-t border-gray-100 flex space-x-3">
              <button
                type="button"
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-600 text-xs font-medium"
                onClick={() => setShowConsentModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white text-xs font-semibold"
                onClick={() => { setTermsAccepted(true); setShowConsentModal(false); }}
              >
                I Agree
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default BasicDetails;
